/**
 * Content tabs component for DEX writer workflow.
 * Switches between File upload and Text input modes.
 */

import { renderFileDrop, initFileDrop } from './file-drop.js';

/**
 * Render content tabs into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {string} state.activeTab - 'file' or 'text'
 * @param {File|null} [state.file] - Selected file (for file tab)
 * @param {string|null} [state.fileName] - File name display override
 * @param {string|null} [state.fileError] - File error message
 * @param {string} [state.textContent] - Text content (for text tab)
 * @param {string|null} [state.textError] - Text error message
 * @param {boolean} [state.disabled] - Whether inputs are disabled
 */
export function renderContentTabs(container, state = {}) {
  const {
    activeTab = 'file',
    file = null,
    fileName = null,
    fileError = null,
    textContent = '',
    textError = null,
    disabled = false
  } = state;

  const isFileActive = activeTab === 'file';
  const isTextActive = activeTab === 'text';

  // Tab styles
  const activeTabClasses = 'border-b-2 border-blue-500 text-blue-600';
  const inactiveTabClasses = 'text-gray-500 hover:text-gray-700';

  container.innerHTML = `
    <div>
      <div class="border-b border-gray-200 mb-4">
        <nav class="flex -mb-px">
          <button
            type="button"
            data-tab="file"
            class="px-4 py-2 text-sm font-medium ${isFileActive ? activeTabClasses : inactiveTabClasses}"
            ${disabled ? 'disabled' : ''}
          >
            File
          </button>
          <button
            type="button"
            data-tab="text"
            class="px-4 py-2 text-sm font-medium ${isTextActive ? activeTabClasses : inactiveTabClasses}"
            ${disabled ? 'disabled' : ''}
          >
            Text
          </button>
        </nav>
      </div>
      <div id="tab-content"></div>
    </div>
  `;

  // Render tab content
  const tabContent = container.querySelector('#tab-content');

  if (isFileActive) {
    // Render file drop zone
    renderFileDrop(tabContent, {
      file,
      fileName,
      error: fileError,
      disabled
    });
  } else {
    // Render text input
    const disabledAttr = disabled ? 'disabled' : '';
    const errorHtml = textError
      ? `<p class="text-red-500 text-xs mt-1">${textError}</p>`
      : '';

    tabContent.innerHTML = `
      <div>
        <textarea
          id="text-content"
          rows="6"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Enter content to encrypt..."
          ${disabledAttr}
        >${escapeHtml(textContent)}</textarea>
        <p class="text-gray-400 text-xs mt-1">Max ~285KB of text</p>
        ${errorHtml}
      </div>
    `;
  }
}

/**
 * Initialize content tabs with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onTabChange - Called when tab changes: (tab) => void
 * @param {Function} callbacks.onFileChange - Called when file selected: (file, error) => void
 * @param {Function} callbacks.onFileClear - Called when file cleared: () => void
 * @param {Function} callbacks.onTextChange - Called when text changes: (text) => void
 * @param {Object} [state] - Current state (needed to know which tab is active)
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initContentTabs(container, callbacks = {}, state = {}) {
  const { onTabChange, onFileChange, onFileClear, onTextChange } = callbacks;
  const { activeTab = 'file' } = state;

  const cleanupFunctions = [];

  // Wire tab button clicks
  const tabButtons = container.querySelectorAll('[data-tab]');
  function handleTabClick(e) {
    const tab = e.target.dataset.tab;
    if (tab && onTabChange) {
      onTabChange(tab);
    }
  }

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', handleTabClick);
  });

  cleanupFunctions.push(() => {
    tabButtons.forEach((btn) => {
      btn.removeEventListener('click', handleTabClick);
    });
  });

  // Wire content based on active tab
  if (activeTab === 'file') {
    // Initialize file drop
    const tabContent = container.querySelector('#tab-content');
    if (tabContent) {
      const fileDropCleanup = initFileDrop(tabContent, {
        onFile: onFileChange,
        onClear: onFileClear
      });
      cleanupFunctions.push(fileDropCleanup);
    }
  } else {
    // Wire textarea blur (not input - avoids re-render on every keystroke)
    const textarea = container.querySelector('#text-content');
    if (textarea) {
      function handleTextBlur(e) {
        if (onTextChange) {
          onTextChange(e.target.value);
        }
      }
      textarea.addEventListener('blur', handleTextBlur);
      cleanupFunctions.push(() => {
        textarea.removeEventListener('blur', handleTextBlur);
      });
    }
  }

  // Return combined cleanup function
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
  };
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
